import React, { forwardRef, useImperativeHandle, useState } from 'react';
import styles from './DataSyncFormSection.module.sass';
import { DropDown, Label, SelectItem } from '@zaiusinc/hera';
import { OptimizelyProduct, ProductInstance, Source, SourceObject } from '../../../../types/DataSyncItem';
import { useObserver } from 'mobx-react-lite';
import Notification from '../Notification';
import { ValidateHandle } from './ValidateHandle';
import { DataSyncObjectsStore } from '../../../../stores/object/DataSyncObjectsStore';
import ProductStoreFactory from '../../../../stores/product/ProductStoreFactory';
import { ObjectIcon, IconSize } from '../ObjectIcon';
import { getNotificationLabelForObject, getObjectLabel } from '../../../../lib/dataSync';
import { directoryStore } from '../../../../stores/DirectoryStore';
import { useSourceAvailability } from '../../../../hooks/useAvailabilityCheck';

export interface DataSyncFormSourceProps {
  initialValue: Source;
  onChange: (value: Source) => void;
  dataSyncObjectsStore: DataSyncObjectsStore | null;
  mode: 'create' | 'edit';
}

const DataSyncFormSource = forwardRef<ValidateHandle, DataSyncFormSourceProps>((props, ref) => {
  const {initialValue, onChange, dataSyncObjectsStore, mode} = props;
  const [source, setSource] = React.useState<Source>({...initialValue});
  const [errors, setErrors] = React.useState<{ [key: string]: boolean }>({
    object_name: false,
  });

  const [objectLabel, setObjectLabel] = useState<string>(getObjectLabel(source.product));

  // Use custom hook for availability checking
  const { instanceUnavailable, objectUnavailable } = useSourceAvailability(source, dataSyncObjectsStore);

  useImperativeHandle(ref, () => ({
    validate: () => {
      if (source.product === OptimizelyProduct.HUB) {
        setErrors({
          product: !source.product,
          instance: false,
          object: !source.object.object_name,
        });

        return !!(!objectUnavailable && source.product && source.object.object_name);

      } else {
        setErrors({
          product: !source.product,
          instance: !source.instance.id,
          object: !source.object.object_name,
        });

        return !!(!instanceUnavailable && !objectUnavailable && source.product
          && source.instance.id && source.object.object_name);
      }
    },
  }));

  const productList = (): SelectItem[] => {
    return ProductStoreFactory.getSourceProductStore().getProductList()
      .filter((product) => {
        // Filter out OCP Database (HUB) for free accounts
        return !directoryStore.showOcpUpgradeLock() || product.value !== OptimizelyProduct.HUB;
      })
      .map((product) => {
        let icon: React.ReactElement = <></>;

        if (product.iconPath) {
          icon = <ObjectIcon size={IconSize.SMALL} src={product.iconPath} alt={product.text}/>;
        }
        return {
          text: product.text,
          value: product.value,
          icon,
        };
      }) || [];
  };

  const instancesList = (): SelectItem[] => {
    const sourceProductStore = ProductStoreFactory.getSourceProductStore();
    const items = sourceProductStore.getInstanceList(
      source.product,
      source.instance?.id,
      source.instance?.name
    );
    if (!items) {
      return [];
    }

    return items.map((product) => {
      let icon: React.ReactElement = <></>;
      if (product.iconPath) {
        icon = (
          <ObjectIcon
            size={IconSize.SMALL}
            src={product.iconPath}
            alt={product.text}
          />
        );
      }
      const isHubApp = source.product === OptimizelyProduct.HUB_APP;
      return {
        text: product.text,
        value: product.value,
        icon,
        disabled: product.unavailable,
        disabledTooltip: product.unavailable
          ? isHubApp
            ? 'This app is no longer available. It may not be installed in this account anymore. Please select a different app.'
            : 'This instance is no longer available. Please select a different instance.'
          : undefined,
      };
    });
  };

  const objectList = (): SelectItem[] => {
    const items = dataSyncObjectsStore?.getSourceObjectList(
      source.object?.object_name,
      source.object?.object_display_name
    );
    if (!items) {
      return [];
    }

    return items.map((object) => {
      let icon: React.ReactElement = <></>;
      if (object.object_icon_url) {
        icon = <ObjectIcon size={IconSize.SMALL} src={object.object_icon_url} alt={object.object_display_name}/>;
      } else if (source.product === OptimizelyProduct.HUB) {
        icon = <ObjectIcon size={IconSize.SMALL} src={'/assets/data_platform.svg'} alt={object.object_display_name}/>;
      }

      return {
        text: object.object_display_name || object.object_name,
        value: object.object_name,
        icon,
        disabled: object.disabled,
        disabledTooltip: object.warning,
      };
    });
  };

  const getInstanceLabel = (): string => {
    switch (source.product) {
      case OptimizelyProduct.HUB_APP:
        return 'App';
      case OptimizelyProduct.CMP:
        return 'Instance';
      case OptimizelyProduct.CGR:
          return 'Instance';
      case OptimizelyProduct.HUB:
        return 'Object';
    }
    return 'Object';
  };

  const getInstanceLoading = (): boolean => {
    if (!source?.product) {
      return false;
    }
    const sourceProductStore = ProductStoreFactory.getSourceProductStore();
    return source.product === OptimizelyProduct.HUB_APP
      ? sourceProductStore.hubAppsLoading
      : sourceProductStore.productInstancesLoading;
  };

  const showInstanceSelection = (): boolean => {
    return source.product === OptimizelyProduct.CMP ||
      source.product === OptimizelyProduct.CGR ||
      source.product === OptimizelyProduct.HUB_APP;
  };

  return useObserver(() => (
    <div className={styles.container}>
      <Label label="Source" subLabel="Select a source of the data.">
        <DropDown
          initialContent="Select Product"
          items={productList()}
          onItemSelect={(item: any) => {
            const newData = { ...source, product: item.value };
            if (source.product !== item.value) {
              newData.instance = {} as ProductInstance;
              newData.object = {} as SourceObject;
              setObjectLabel(getObjectLabel(item.value));
            }
            setSource(newData);
            onChange(newData);
            setErrors({ ...errors, product: false });
          }}
          value={source.product}
          width={390}
          disabled={mode === 'edit'}
        />
        <Notification
          message="Please select a product"
          show={errors.product}
          type="error"
        />
      </Label>

      {showInstanceSelection() && (
        <Label label={getInstanceLabel()}>
          <DropDown
            initialContent={'Select ' + getInstanceLabel()}
            items={instancesList()}
            loading={getInstanceLoading()}
            className={instanceUnavailable ? styles.unavailableSelected : ''}
            onItemSelect={function noRefCheck(item: any) {
              const newData = {
                ...source,
                instance: { id: item.value, name: item.text },
              };
              if (source.instance.id !== item.value) {
                newData.object = {} as SourceObject;
              }
              setSource(newData);
              onChange(newData);
              setErrors({ ...errors, instance: false });
            }}
            value={source.instance.id}
            width={390}
            disabled={mode === 'edit'}
          />
          <Notification
            message="Please select an instance"
            show={errors.instance}
            type="error"
          />
          <Notification
            message={
              source.product === OptimizelyProduct.HUB_APP
                ? 'The app this sync is configured for is no longer available. It may not be installed in this account anymore.'
                : 'The instance this sync is configured for is no longer available. Either you don\'t have access to the instance or it does not exist anymore.'
            }
            show={instanceUnavailable}
            type="warning"
          />
        </Label>
      )}

      <Label label={objectLabel}>
        <DropDown
          filterable
          loading={dataSyncObjectsStore?.loading || false}
          initialContent={`Select ${objectLabel}`}
          value={source.object.object_name}
          items={objectList()}
          width={390}
          disabled={mode === 'edit'}
          className={objectUnavailable ? styles.unavailableSelected : ''}
          onItemSelect={(item) => {
            const newSource = {
              ...source,
              object: {
                ...source.object,
                object_name: item.value,
                object_display_name: item.text,
              },
            };
            setSource(newSource);
            onChange(newSource);
            setErrors({ ...errors, object_name: false });
          }}
        />
        <Notification
          message={`Please select ${getNotificationLabelForObject(
            source.product
          )}`}
          show={errors.object_name}
          type="error"
        />
        <Notification
          message={
            source.product === OptimizelyProduct.CGR
              ? 'The content type this sync is configured for is no longer available. Either you don\'t have access to the content type or it does not exist anymore.'
              : 'The object this sync is configured for is no longer available. Either you don\'t have access to the object or it does not exist anymore.'
          }
          show={objectUnavailable}
          type="warning"
        />
      </Label>
    </div>
  ));
});

export default DataSyncFormSource;
